'use client';

import { Warning } from '@phosphor-icons/react/ssr';
import { createContext, type ReactNode, useContext, useId } from 'react';

import { tv } from '../../lib/tv';
import { Label } from '../ui/label';

type FieldContextValue = {
  id: string;
  labelId: string;
  messageId: string;
  hasMessage: boolean;
  invalid: boolean;
  disabled: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Props a control inside a Field picks up: its id (for the label), the
 * message it is described by, and the invalid / disabled status. Explicit
 * props on the control still win.
 */
export function useFieldControl<P extends { id?: string; disabled?: boolean }>(props: P) {
  const field = useContext(FieldContext);
  if (!field) return props;
  return {
    ...props,
    id: props.id ?? field.id,
    disabled: props.disabled ?? field.disabled,
    'aria-invalid':
      field.invalid ||
      (props as { 'aria-invalid'?: boolean })['aria-invalid'] ||
      undefined,
    'aria-describedby': field.hasMessage ? field.messageId : undefined,
  };
}

/**
 * The id of the enclosing Field's label, for popups that need a name of
 * their own (a Select's listbox, a MultiSelect's panel).
 */
export function useFieldLabelId(): string | undefined {
  return useContext(FieldContext)?.labelId;
}

export type FieldProps = {
  /** Visible label - always present; placeholder text is never a label. */
  label: ReactNode;
  /** Shows the "Optional" marker after the label. */
  optional?: boolean;
  /** Explains format or purpose. Replaced by `error` when that is set. */
  helper?: ReactNode;
  /** Error message - says how to fix the problem. Marks the control invalid. */
  error?: ReactNode;
  /** Current length, for the counter. Shown with `maxLength` as "12/280". */
  count?: number;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  /** The control: a TextInput or TextArea (or any control using useFieldControl). */
  children: ReactNode;
};

const field = tv({
  slots: {
    root: 'flex flex-col gap-1.5',
    header: 'flex items-center gap-1.5',
    label: 'text-label-md text-text-primary',
    optional: 'text-caption text-text-tertiary',
    message: 'flex items-center gap-1.5',
    messageText: 'text-caption text-text-secondary flex-1',
    icon: 'text-status-danger-fg size-3.5 shrink-0',
    counter: 'text-caption text-text-tertiary tabular-nums',
  },
  variants: {
    invalid: { true: { messageText: 'text-status-danger-fg' } },
    disabled: {
      true: { label: 'text-text-disabled', messageText: 'text-text-disabled' },
    },
  },
});

/**
 * Label + control + helper or error message, with an optional character
 * counter (Figma: Components ↳ Inputs ↳ Field). Wires the label, message and
 * status to the control it wraps.
 */
export function Field({
  label,
  optional,
  helper,
  error,
  count,
  maxLength,
  disabled = false,
  className,
  children,
}: FieldProps) {
  const id = useId();
  const invalid = Boolean(error);
  const message = error ?? helper;
  const showCounter = count !== undefined && maxLength !== undefined;
  const styles = field({ invalid, disabled });
  const context: FieldContextValue = {
    id: `${id}-control`,
    labelId: `${id}-label`,
    messageId: `${id}-message`,
    hasMessage: Boolean(message),
    invalid,
    disabled,
  };

  return (
    <FieldContext value={context}>
      <div
        data-slot="field"
        data-invalid={invalid || undefined}
        // A disabled field is a disabled group. Its label and message carry
        // data-disabled: WCAG exempts text of inactive components from
        // contrast minimums, and the Storybook axe config honours the marker.
        role={disabled ? 'group' : undefined}
        aria-disabled={disabled || undefined}
        className={styles.root({ className })}
      >
        <div className={styles.header()}>
          <Label
            id={context.labelId}
            htmlFor={context.id}
            data-disabled={disabled || undefined}
            className={styles.label()}
          >
            {label}
          </Label>
          {optional && <span className={styles.optional()}>Optional</span>}
        </div>
        {children}
        {(message || showCounter) && (
          <div className={styles.message()}>
            {invalid && <Warning aria-hidden className={styles.icon()} />}
            <p
              id={context.messageId}
              data-disabled={disabled || undefined}
              className={styles.messageText()}
              role={invalid ? 'alert' : undefined}
            >
              {message}
            </p>
            {showCounter && (
              <span className={styles.counter()} aria-hidden>
                {count}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    </FieldContext>
  );
}
